/*
 * Renaming a channel (X-A4 N14). It is a dialog rather than another field in the About block because
 * it is the one change in this column that breaks something: the short name is the address, so every
 * link anybody ever pasted to `#alt-slug` stops working the moment it is taken. The dialog says that
 * in a line of its own, with the old address in it, and it says it only while the short name is
 * really being changed — a warning that stands there while somebody fixes a typo in the display name
 * is a warning nobody reads the next time.
 *
 * The slug rule is checked here before the request (`isValidSlug`, the client's copy of
 * `slugPattern`) so that a malformed address is a hint under the field rather than a round trip. The
 * two rules only the server can apply — a name already taken, a reserved segment — come back as the
 * refusal's own key and are shown in the form, where the person is looking, and never as a notice
 * that has gone by the time it is read.
 *
 * The address changes under the reader's feet, so the dialog navigates to the new one itself. The
 * conversation's meta arrives over the socket either way; what would otherwise happen is a route
 * that still names the old slug and a screen that says the conversation does not exist.
 *
 * The surface mounts this component only while it is open, so the two fields start from what the
 * conversation is called *now* every time — no effect that copies props into state, and no draft of a
 * rename somebody cancelled offered back as though it had been saved.
 */

import { useState } from "react"
import { t as coreT } from "../../../core/i18n/index.ts"
import { useAsyncAction } from "../../../lib/useAsyncAction.ts"
import { Button, Dialog, Field, FormAlert } from "../../../ui/index.ts"
import { t } from "../i18n/t.ts"
import { conversationPath } from "../navigation.ts"
import { isValidSlug, SLUG_MAX } from "../sidebar/slug.ts"
import type { ConversationView } from "../wire/views.ts"
import { NAME_MAX } from "./api.ts"
import "./conversation.css"
import { updateConversation } from "./manage.ts"
import { goTo } from "./opening.ts"

export function RenameDialog({ conv, onClose }: { conv: ConversationView; onClose: () => void }) {
	const currentName = conv.name ?? ""
	const currentSlug = conv.slug ?? ""
	const [name, setName] = useState(currentName)
	const [slug, setSlug] = useState(currentSlug)

	const slugChanged = slug !== currentSlug
	const rename = useAsyncAction(async () => {
		await updateConversation(conv.id, { name: name.trim(), slug })
		onClose()
		if (slugChanged) goTo(conversationPath(slug))
	})

	const canSubmit = name.trim() !== "" && isValidSlug(slug) && !rename.busy

	return (
		<Dialog
			open
			onOpenChange={(next) => {
				if (!next) onClose()
			}}
			title={t("chat.details.renameTitle")}
			description={t("chat.details.renameBody")}
			footer={
				<div className="tb-dialog__actions">
					<Button variant="neutral" label={coreT("action.cancel")} onClick={onClose} />
					<Button
						variant="primary"
						label={t("chat.details.renameSave")}
						disabled={!canSubmit}
						onClick={() => void rename.run()}
					/>
				</div>
			}
		>
			<form
				className="tb-fields"
				onSubmit={(event) => {
					event.preventDefault()
					if (canSubmit) void rename.run()
				}}
			>
				<Field label={t("chat.details.renameName")}>
					{(props) => (
						<input
							{...props}
							className="tb-field__control"
							value={name}
							maxLength={NAME_MAX}
							onChange={(event) => setName(event.target.value)}
						/>
					)}
				</Field>
				<Field
					label={t("chat.details.renameSlug")}
					hint={t("chat.details.renameSlugHint")}
					{...(slug !== "" && !isValidSlug(slug)
						? { error: t("chat.details.renameSlugInvalid") }
						: {})}
				>
					{(props) => (
						<input
							{...props}
							className="tb-field__control"
							value={slug}
							maxLength={SLUG_MAX}
							onChange={(event) => setSlug(event.target.value)}
						/>
					)}
				</Field>
				{slugChanged ? (
					<p className="tb-inline-edit__warning">
						{t("chat.details.renameBreaksLinks", { slug: currentSlug })}
					</p>
				) : null}
				<FormAlert messageKey={rename.error} />
			</form>
		</Dialog>
	)
}
